import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import { ErrorBanner } from './ErrorBanner'

// Client-side resend cooldown, aligned with the server's 60s per-uid cooldown
// (VERIFY_CODE_RESEND_COOLDOWN) so the button is disabled while a resend would
// be rejected anyway.
const RESEND_COOLDOWN_SECONDS = 60

// verifyErrorMessage maps the owner-plane verify error codes (verify_email.go,
// D13 — not in the SDK BacklitError allowlist) to friendly copy, falling back
// to the ApiError message / a generic line. Owned here so both the Login verify
// step and the VerifyEmailBanner share one mapping.
function verifyErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'invalid_code':
        return 'That code is incorrect. Check it and try again.'
      case 'code_expired':
        return 'That code has expired. Request a new one.'
      case 'too_many_attempts':
        return 'Too many incorrect attempts. Request a new code.'
      case 'too_many_requests':
        return 'Too many requests — please wait a moment and try again.'
      case 'email_send_failed':
        return 'We couldn’t send the code. Please try again.'
    }
    return e.message
  }
  return 'Something went wrong. Please try again.'
}

// VerifyCodeForm is the shared inline 6-digit code entry used by both the
// post-signup Login step and the dashboard VerifyEmailBanner. A single
// paste/autofill-friendly input (autocomplete=one-time-code), a Verify submit,
// and a Resend button gated by a client cooldown countdown. On a successful
// confirm the AuthContext force-refreshes the ID token (so the next call clears
// the CA3 gate) and calls onVerified.
export function VerifyCodeForm({
  onVerified,
  autoSend = false,
  changeEmail,
}: {
  onVerified?: () => void
  // When true, request one code on mount (entering the verify step).
  autoSend?: boolean
  // Optional affordance rendered next to "Resend code" (e.g. "Change email").
  changeEmail?: ReactNode
}) {
  const auth = useAuth()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [cooldown, setCooldown] = useState(0)
  const sentOnce = useRef(false)

  // Tick the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return
    const t = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000)
    return () => clearInterval(t)
  }, [cooldown])

  const send = async () => {
    setError(null)
    setNotice(null)
    try {
      await auth.sendVerifyCode()
      setNotice('We sent a 6-digit code to your email. Enter it below.')
      setCooldown(RESEND_COOLDOWN_SECONDS)
    } catch (e) {
      setError(verifyErrorMessage(e))
    }
  }

  // Auto-request one code on entering the step. The ref guards against a double
  // send under React StrictMode's dev double-invoke (same instance, ref persists).
  useEffect(() => {
    if (autoSend && !sentOnce.current) {
      sentOnce.current = true
      void send()
    }
    // send is intentionally not a dependency — we want exactly one auto-send.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSend])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      await auth.confirmVerifyCode(code.trim())
      onVerified?.()
    } catch (err) {
      setError(verifyErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="space-y-3" onSubmit={submit}>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {notice && !error && (
        <p role="status" className="text-sm text-glow-200">
          {notice}
        </p>
      )}
      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="123456"
        aria-label="6-digit verification code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        required
      />
      <Button type="submit" className="w-full" disabled={busy || code.trim().length !== 6}>
        {busy ? 'Verifying…' : 'Verify'}
      </Button>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <button
          type="button"
          className="font-medium text-glow-600 hover:underline disabled:opacity-50"
          disabled={cooldown > 0}
          onClick={() => void send()}
        >
          {cooldown > 0 ? `Resend code (${cooldown}s)` : 'Resend code'}
        </button>
        {changeEmail}
      </div>
    </form>
  )
}
